import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useScheduleMeeting } from '@/features/meetings/useMeetings';
import { extractError } from '@/lib/axios';
import type { CreateScheduledPayload } from '@/features/meetings/meeting.api';

interface FormValues extends CreateScheduledPayload {}

export default function SchedulePage(): JSX.Element {
  const schedule = useScheduleMeeting();
  const navigate = useNavigate();
  const { register, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: {
      waitingRoom: true,
      allowGuests: true,
      muteOnEntry: false,
      recurrence: 'NONE',
      maxParticipants: 100,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const m = await schedule.mutateAsync({
        ...values,
        scheduledStart: new Date(values.scheduledStart).toISOString(),
        scheduledEnd: new Date(values.scheduledEnd).toISOString(),
        maxParticipants: Number(values.maxParticipants),
      });
      toast.success('Meeting scheduled');
      navigate(`/meetings`);
      return m;
    } catch (err) {
      toast.error(extractError(err).message);
    }
  });

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="text-2xl font-bold">Schedule a meeting</h1>
      <form onSubmit={onSubmit} className="card mt-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Title</label>
          <input className="input" {...register('title', { required: true })} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Description</label>
          <textarea className="input" rows={3} {...register('description')} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Start</label>
            <input className="input" type="datetime-local" {...register('scheduledStart', { required: true })} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">End</label>
            <input className="input" type="datetime-local" {...register('scheduledEnd', { required: true })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Recurrence</label>
            <select className="input" {...register('recurrence')}>
              <option value="NONE">None</option>
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Max participants</label>
            <input className="input" type="number" min={2} max={1000} {...register('maxParticipants')} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Password (optional)</label>
          <input className="input" type="text" {...register('password')} />
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" {...register('waitingRoom')} /> Waiting room
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" {...register('muteOnEntry')} /> Mute on entry
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" {...register('allowGuests')} /> Allow guests
          </label>
        </div>
        <button type="submit" className="btn-primary" disabled={formState.isSubmitting}>
          {formState.isSubmitting ? 'Scheduling…' : 'Schedule meeting'}
        </button>
      </form>
    </div>
  );
}
